import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { marked } from 'marked'

import { SfLog } from '@app/adapters/logger'
import type {
  Proposal,
  ProposalStatus,
} from '@app/application/client_meetings/types'
import type { Meeting as PastMeeting } from '@app/application/client_meetings/api/past'
import * as ProposalsApi from '@app/application/client_meetings/api/proposals'

export const useProposalDetailsStore = defineStore('proposalDetails', () => {
  const todosByProposalId = ref<Record<string, string[]>>({})
  const htmlTodosByProposalId = ref<Record<string, string[]>>({})
  const statusByProposalId = ref<Record<string, ProposalStatus>>({})
  const repliesByProposalIdTalkingPointId = ref<
    Record<string, Record<number, string>>
  >({})
  const htmlRepliesByProposalIdTalkingPointId = ref<
    Record<string, Record<number, string>>
  >({})

  const apiProposalDetailsById = computed(() => {
    const details: Record<string, ProposalsApi.ProposalDetails> = {}

    for (const proposalId in todosByProposalId.value) {
      details[proposalId] = {
        follow_up_actions: todosByProposalId.value[proposalId],
        replies: Object.keys(
          repliesByProposalIdTalkingPointId.value[proposalId]
        ).map((talkingPointId) => ({
          talking_point_id: parseInt(talkingPointId, 10),
          message:
            repliesByProposalIdTalkingPointId.value[proposalId][
              parseInt(talkingPointId, 10)
            ],
        })),
      }
    }
    return details
  })

  async function addProposalDetails(proposal: Proposal): Promise<void> {
    statusByProposalId.value[proposal.id] = proposal.status

    if (!proposal.details) {
      return
    }

    const followUpActions = [...proposal.details.followUpActions]
    const replies = proposal.details.replies.reduce((acc, reply) => {
      acc[reply.talkingPointId] = reply.message
      return acc
    }, {} as Record<string, string>)
    todosByProposalId.value[proposal.id] = followUpActions
    repliesByProposalIdTalkingPointId.value[proposal.id] = replies

    if (followUpActions.length > 0) {
      htmlTodosByProposalId.value[proposal.id] = await Promise.all(
        followUpActions.map(async (todo: string) => {
          try {
            return await marked.parse(todo)
          } catch (error) {
            SfLog.error('Error parsing todo markdown:', { error })
            return todo
          }
        })
      )
    }

    if (Object.keys(replies).length > 0) {
      const result: Record<string, string> = {}
      await Promise.all(
        Object.keys(replies).map(async (talkingPointId) => {
          try {
            result[talkingPointId] = await marked.parse(replies[talkingPointId])
          } catch (error) {
            SfLog.error('Error parsing reply markdown:', { error })
            result[talkingPointId] = replies[talkingPointId]
          }
        })
      )
      htmlRepliesByProposalIdTalkingPointId.value[proposal.id] = result
    }
  }

  async function updateProposal(proposalId: number): Promise<void> {
    await ProposalsApi.update(
      proposalId,
      apiProposalDetailsById.value[proposalId]
    )
  }

  async function deleteFollowUpAction(
    proposalId: number,
    index: number
  ): Promise<void> {
    const todos = [...todosByProposalId.value[proposalId]]
    const htmlTodos = [...htmlTodosByProposalId.value[proposalId]]

    if (!todos) {
      SfLog.error(
        `Cannot delete follow-up action: proposal ${proposalId} not found`
      )
      return
    }

    if (index < 0 || index >= todos.length) {
      SfLog.error(`Invalid follow-up action index: ${index}`)
      return
    }

    todosByProposalId.value[proposalId].splice(index, 1)
    htmlTodosByProposalId.value[proposalId].splice(index, 1)

    try {
      await updateProposal(proposalId)
    } catch (error) {
      // revert state and log error
      todosByProposalId.value[proposalId] = todos
      htmlTodosByProposalId.value[proposalId] = htmlTodos
      SfLog.error('Failed to update proposal:', { error })
    }
  }

  async function deleteReply(
    proposalId: number,
    talkingPointId: number
  ): Promise<void> {
    const replies = { ...repliesByProposalIdTalkingPointId.value[proposalId] }
    const htmlReplies = {
      ...htmlRepliesByProposalIdTalkingPointId.value[proposalId],
    }

    if (!replies) {
      SfLog.error(`Cannot delete reply: proposal ${proposalId} not found`)
      return
    }

    if (!(talkingPointId in replies)) {
      SfLog.error(`Reply for talking point ${talkingPointId} not found`)
      return
    }

    delete repliesByProposalIdTalkingPointId.value[proposalId][talkingPointId]
    delete htmlRepliesByProposalIdTalkingPointId.value[proposalId][
      talkingPointId
    ]

    try {
      await updateProposal(proposalId)
    } catch (error) {
      // revert state and log error
      repliesByProposalIdTalkingPointId.value[proposalId] = replies
      htmlRepliesByProposalIdTalkingPointId.value[proposalId] = htmlReplies
      SfLog.error('Failed to update proposal:', { error })
    }
  }

  async function acceptProposal(
    proposalId: number
  ): Promise<PastMeeting | null> {
    try {
      const meeting = await ProposalsApi.accept(proposalId)
      window.eventHub.$emit('refreshCommunicationData')

      return meeting
    } catch (error) {
      SfLog.error('Failed to accept proposal:', { error })
      throw error
    }
  }

  async function rejectProposal(
    proposalId: number
  ): Promise<PastMeeting | null> {
    try {
      return await ProposalsApi.reject(proposalId)
    } catch (error) {
      SfLog.error('Failed to reject proposal:', { error })
      throw error
    }
  }

  return {
    todosByProposalId,
    htmlTodosByProposalId,
    repliesByProposalIdTalkingPointId,
    htmlRepliesByProposalIdTalkingPointId,
    statusByProposalId,
    apiProposalDetailsById,
    addProposalDetails,
    deleteFollowUpAction,
    deleteReply,
    acceptProposal,
    rejectProposal,
  }
})
